import { dedent } from 'ts-dedent'
import { DataWarehouseConnection, EXTENSIONS_TABLE_NAME } from '..'
import { Workflow } from '../..'
import { WorkflowComponent } from '../../../components/common/workflowcomponent'
import { ReadTable } from '../../../components/readtable'
import {
  ColumnDataType,
  LocationType,
  NumericType,
  Provider,
  StoredProcedureScope,
  TableOptimizationColumnDataType,
  TableOptimizationInfo,
  WorkflowCodeType,
  WorkflowComponentParamType,
  WorkflowComponentValue,
  WorkflowEnvVars,
  WorkflowTableSchemas,
  WorkflowVariable
} from '../../../types'
import { OUTPUT_TABLE_FQN_VAR_NAME, OUTPUT_TABLE_NAME_VAR_NAME, formatVariableValue } from '../../../utils'

export class SFConnection extends DataWarehouseConnection {
  constructor(name: string, tempLocation: string, workspaceLocation: string, extensionsLocation: string) {
    super(name, tempLocation, workspaceLocation, extensionsLocation, Provider.Snowflake)
  }

  castValue(value: string, type: string) {
    return `(${value})::${type}`
  }

  getTempSelectQuery(select: string, dryRun: boolean): string {
    if (dryRun) {
      return dedent`SELECT * FROM
                      (
                        ${select}
                      )
                    WHERE 1=0`
    } else {
      return select
    }
  }

  getOutputTableName(
    workflowId: string,
    componentName: string,
    params: Record<string, unknown>,
    output: string,
    codeType: WorkflowCodeType
  ): string {
    const name = super.getOutputTableName(workflowId, componentName, params, output, codeType)
    // in SF we use the full names also for temp tables
    if (codeType === WorkflowCodeType.StoredProcedure) {
      return `${this.tempLocation}.${name}`.toUpperCase()
    } else {
      return name.toUpperCase()
    }
  }

  getCreateTempTableQuery(
    tablename: string,
    select: string,
    codeType: WorkflowCodeType,
    optimizeBy: TableOptimizationInfo | null,
    addOptimizationStats?: boolean
  ): string {
    select = dedent(select)
    const dryRun = codeType === WorkflowCodeType.DryRun
    let create = ''
    if (codeType === WorkflowCodeType.StoredProcedure) {
      create = `CREATE OR REPLACE TEMPORARY TABLE`
    } else if (codeType === WorkflowCodeType.FullNoCache) {
      create = 'CREATE OR REPLACE TRANSIENT TABLE'
    } else {
      create = 'CREATE TRANSIENT TABLE IF NOT EXISTS'
    }
    let query = ''
    if (!dryRun && optimizeBy?.sfSearchOptimizationColumn) {
      query = dedent`${create} ${tablename}
                AS
                SELECT * FROM (
                  ${this.getTempSelectQuery(select, dryRun)}
                )
                ORDER BY ST_GEOHASH(${optimizeBy.sfSearchOptimizationColumn.name});
                BEGIN
                  ALTER TABLE ${tablename}
                    ADD search optimization
                    ON GEO(${optimizeBy.sfSearchOptimizationColumn.name});
                EXCEPTION WHEN OTHER THEN
                    NULL;
                END;`
    } else {
      query = dedent`${create} ${tablename}
                 AS
                  ${this.getTempSelectQuery(select, dryRun)};`
    }
    if (!dryRun && optimizeBy?.column?.type === TableOptimizationColumnDataType.NonGeo) {
      query += `ALTER TABLE ${tablename} CLUSTER BY (${optimizeBy.column.name});`
    }
    return query
  }

  getCreateTableQuery(
    tablename: string,
    select: string,
    codeType: WorkflowCodeType,
    optimizeBy: TableOptimizationInfo | null,
    addOptimizationStats?: boolean
  ): string {
    return this.getCreateTempTableQuery(tablename, select, codeType, optimizeBy, addOptimizationStats)
      .replace(RegExp('TRANSIENT ', 'g'), '')
      .replace(RegExp('TEMPORARY ', 'g'), '')
  }

  wrapComponentCodeInFlowControlBlock(query: string, codeType: WorkflowCodeType): string {
    if (codeType === WorkflowCodeType.DryRun) {
      return dedent`
          BEGIN
            ${query ?? 'SELECT 1;'}
          EXCEPTION
          WHEN statement_error THEN
            SELECT 1;
            -- do nothing
          WHEN OTHER THEN
            SELECT 1;
            -- do nothing
          END;`
    } else {
      return dedent`
          BEGIN
            ${query}
          END;`
    }
  }

  getStoredProcedureOutputTableNameExpression(workflow: Workflow, full: boolean, scope: StoredProcedureScope): string {
    const publicVars = workflow.variables.filter((v) => v.scopes?.includes(scope))
    let root = workflow.getStoredProcedureOutputTableFullName(this, scope)
    if (!full) {
      root = root.split('.').pop() as string
    }
    if (publicVars.length === 0) {
      return `'${root}_' || SUBSTRING(MD5(''), 1, 16)`
    } else {
      return `'${root}_' || SUBSTRING(MD5(${publicVars
        .map((v) => this.quoteVariableName(v.name))
        .join(" || ',' || ")}), 1, 16)`
    }
  }

  wrapCodeInIfTableExistsBlock(
    query: string,
    outputTables: Array<string>,
    codeType: WorkflowCodeType,
    outputTablesExpressions?: Array<string>,
    variablePrefix?: string
  ): string {
    variablePrefix = variablePrefix || ''
    if ([WorkflowCodeType.DryRun, WorkflowCodeType.StoredProcedure].includes(codeType)) {
      return query
    } else if (codeType === WorkflowCodeType.FullNoCache) {
      return dedent`
        ${outputTables.map((x) => `DROP TABLE IF EXISTS ${this.quoteName(x)};`).join('\n')}
        ${query}
      `
    } else {
      const outputTablesLiteralNames = outputTables.map((x) => `'\\'${x.split('.').pop()?.toUpperCase()}\\''`)
      const outputTablesString = [
        ...outputTablesLiteralNames,
        ...(outputTablesExpressions?.map((v) => `'\\'' || UPPER(${v}) || '\\''`) || [])
      ].join(" || ',' || ")

      const [database, schema] = this.tempLocation.split('.')
      return dedent`
          DECLARE
            ${variablePrefix}query STRING;
            ${variablePrefix}tablesCount INTEGER;
            ${variablePrefix}tablesNames STRING;
          BEGIN
            ${variablePrefix}tablesNames := ${outputTablesString};
            ${variablePrefix}query := '
              SELECT COUNT(1) AS cnt
              FROM
                ${database}.INFORMATION_SCHEMA.TABLES
              WHERE
                table_name IN (' || ${variablePrefix}tablesNames || ')
                AND table_schema ILIKE ''${schema}''
            ';
            DECLARE
              rs resultset DEFAULT (EXECUTE IMMEDIATE :${variablePrefix}query);
              cur CURSOR FOR rs;
            BEGIN
              FOR result_row IN cur DO
                ${variablePrefix}tablesCount := result_row.cnt;
              END FOR;
            END;
            IF (${variablePrefix}tablesCount < ${outputTables.length + (outputTablesExpressions?.length || 0)}) THEN
              BEGIN
                ${query}
              END;
            END IF;
          END;`
    }
  }

  async getWorkflowCode(
    workflow: Workflow,
    envVars: WorkflowEnvVars,
    codeType: WorkflowCodeType,
    schemas?: WorkflowTableSchemas,
    preQuery?: string
  ): Promise<string> {
    const queries = await workflow.getSubqueries(this, envVars, codeType, schemas)
    const executable = queries.some((q) => q.query)
    if (executable) {
      if (codeType !== WorkflowCodeType.StoredProcedure) {
        let query = await this._getScriptBody(workflow, envVars, queries, codeType, schemas)
        query = dedent`
                /*
                ${workflow.workflowMetadataAsCommentString(codeType)}
                */
                BEGIN
                  ${preQuery || ''}${query}
                END;`
        query = this.replaceVariablesInScriptQuery(query, workflow.variables)
        return query
      } else {
        return await this._getProcedureBody(workflow, envVars, queries, schemas)
      }
    }
    return ''
  }

  replaceVariablesInStoredProcedureQuery(
    variables: Array<WorkflowVariable>,
    query: string,
    requiresDynamicSQL: boolean
  ) {
    if (!query || !Array.isArray(variables) || variables.length === 0) {
      return query || ''
    }

    // Sort by name length (descending) to avoid substring replacement issues
    const sortedVariables = [...variables].sort((a, b) => b.name.length - a.name.length)

    if (sortedVariables.length === 0) {
      return query
    }

    if (requiresDynamicSQL) {
      query = dedent(query).replace(/'/g, "\\'")
      query = this.executeDynamicSQL({ sql: query, addQuotes: true })
    }

    for (const variable of sortedVariables) {
      // Replace the following patterns with the following priority:
      // 1. Pattern for "SELECT * FROM @variable"
      // 2. Pattern for variable inside JSON strings
      // 3. Pattern for generic variable values

      if (requiresDynamicSQL) {
        const replacement = `' || ${this.quoteVariableName(variable.name)} || '` // Review usage of :var notation
        query = query
          .replace(new RegExp(`SELECT\\s+\\*\\s+FROM\\s+@${variable.name}`, 'gi'), `SELECT * FROM ${replacement}`)
          .replace(new RegExp(`"@${variable.name}"`, 'g'), `"${replacement}"`)
          .replace(
            new RegExp(`@${variable.name}`, 'g'),
            variable.type === WorkflowComponentParamType.String ? `''${replacement}''` : replacement
          )
      } else {
        const replacement = `:${variable.name}`
        query = query
          .replace(new RegExp(`SELECT\\s+\\*\\s+FROM\\s+@${variable.name}`, 'gi'), `SELECT * FROM ${replacement}`)
          .replace(new RegExp(`"@${variable.name}"`, 'g'), `"' || ${replacement} || '"`)
          .replace(new RegExp(`@${variable.name}`, 'g'), `${replacement}`)
      }
    }

    return query
  }

  paramTypeToDataProviderType(inputType: WorkflowComponentParamType) {
    switch (inputType) {
      case WorkflowComponentParamType.String:
        return 'STRING'
      case WorkflowComponentParamType.Number:
        return 'FLOAT'
      case WorkflowComponentParamType.Boolean:
        return 'BOOLEAN'
      default:
        return 'STRING'
    }
  }

  columnTypeToDataProviderType(colType: ColumnDataType) {
    switch (colType) {
      case ColumnDataType.String:
        return 'STRING'
      case ColumnDataType.Number:
        return 'FLOAT'
      case ColumnDataType.Boolean:
        return 'BOOLEAN'
      case ColumnDataType.Geography:
        return 'GEOGRAPHY'
      case ColumnDataType.Binary:
        return 'BINARY'
      case ColumnDataType.Date:
        return 'DATE'
      case ColumnDataType.Time:
        return 'TIME'
      case ColumnDataType.Timestamp:
        return 'TIMESTAMP'
      default:
        return 'STRING'
    }
  }

  wrapCodeInProcedure(
    name: string,
    workflow: Workflow,
    body: string,
    includeDiagram: boolean,
    isImmutable: boolean,
    scope: StoredProcedureScope
  ): string {
    const procedureParams = workflow.variables
      .filter((v) => v.scopes?.includes(scope))
      .map((input) => `${this.quoteVariableName(input.name)} ${this.paramTypeToDataProviderType(input.type)}`)
    const declareVariables = workflow.variables
      .filter((v) => !v.scopes?.includes(scope))
      .map(
        (input) =>
          `${this.quoteVariableName(input.name)} ${this.paramTypeToDataProviderType(
            input.type
          )} DEFAULT ${formatVariableValue(input)};`
      )
    const defaultValues = workflow.variables
      .filter((v) => v.scopes?.includes(scope))
      .map(
        (input) =>
          `${this.quoteVariableName(input.name)} := COALESCE(${this.quoteVariableName(
            input.name
          )}, ${formatVariableValue(input)});`
      )
    const hasOutput = workflow.hasStoredProcedureOutputTable(scope)
    let outputTableVars = ''
    if (hasOutput || isImmutable) {
      declareVariables.push(`${OUTPUT_TABLE_NAME_VAR_NAME} STRING;`)
      declareVariables.push(`${OUTPUT_TABLE_FQN_VAR_NAME} STRING;`)
      const outputTableNameVariable = `${OUTPUT_TABLE_NAME_VAR_NAME} := ${this.getStoredProcedureOutputTableNameExpression(
        workflow,
        false,
        scope
      )};`
      const outputTableFqnVariable = `${OUTPUT_TABLE_FQN_VAR_NAME} := ${this.getStoredProcedureOutputTableNameExpression(
        workflow,
        true,
        scope
      )};`
      outputTableVars = dedent`${outputTableNameVariable}
                               ${outputTableFqnVariable}`
    }
    const comment = this._getStoredProcedureEmbeddedComment(workflow)
    const returnTable = hasOutput
      ? workflow.getStoredProcedureOutputIsTableFullContent(this, scope)
        ? dedent`
          LET query := REPLACE(
            'SELECT * FROM ##TABLENAME##',
            '##TABLENAME##',
            __outputtablefqn
          );
          EXECUTE IMMEDIATE query;
          LET results RESULTSET:= (SELECT * FROM TABLE(RESULT_SCAN(LAST_QUERY_ID())));
          RETURN TABLE(results);
          `
        : dedent`
          RETURN ${OUTPUT_TABLE_FQN_VAR_NAME};`
      : ''
    return dedent`
                CREATE OR REPLACE PROCEDURE
                  ${this.workspaceLocation}.${name}(
                    ${procedureParams.join(',\n')}
                  )
                  RETURNS ${workflow.getStoredProcedureOutputIsTableFullContent(this, scope) ? 'TABLE()' : 'VARCHAR'}
                  LANGUAGE SQL
                  AS
                  $$
                  ${includeDiagram ? comment : ''}
                  ${
                    declareVariables.length > 0
                      ? dedent`
                        DECLARE
                          ${declareVariables.join('\n')}`
                      : ''
                  }
                  BEGIN
                    ${defaultValues.join('\n')}
                    ${outputTableVars}
                    ${body}
                    ${returnTable}
                  END;
                  $$`
  }

  extractErrorLineNumberFromApiResponse(response: string): number | null {
    const match = response.match(/line (\d+) at/)
    if (match) {
      return parseInt(match[1]) + 6 // header lines are not considered by Snowflake, so we have to add them here
    } else {
      return null
    }
  }

  getSourceTablePermissionsQuery(workflow: Workflow) {
    const conditions: Record<string, Array<string>> = {}
    const tablenames: Array<string> = []

    for (const [componentName, component] of Object.entries(workflow.components)) {
      if (component.metadata.name === new ReadTable().metadata.name) {
        const value = workflow.componentValues[componentName]['source']
        const tablename = (value as WorkflowComponentValue).value as string
        tablenames.push(tablename)
        const [catalog, schema, name] = tablename.toUpperCase().split('.')
        if (!(catalog in conditions)) {
          conditions[catalog] = []
        }
        conditions[catalog].push(
          dedent`(TABLE_CATALOG = '${catalog}' AND TABLE_SCHEMA = '${schema}' AND TABLE_NAME = '${name}')`
        )
      }
    }
    if (tablenames.length === 0) {
      return ''
    }
    const accessible = Object.keys(conditions)
      .map((k) => {
        return dedent`SELECT TABLE_CATALOG || '.' || TABLE_SCHEMA || '.' || TABLE_NAME AS name
                FROM ${k}.INFORMATION_SCHEMA.TABLES
                WHERE ${conditions[k].join('\nOR\n')}`
      })
      .join('\nUNION ALL\n')

    const query = dedent`WITH _accessible AS (
              ${accessible}
            ),
            _names AS (
              SELECT name
              FROM (
                ${tablenames.map((x) => dedent`SELECT '${x}' AS name`).join('\nUNION ALL\n')}
              )
            )
            SELECT ARRAY_AGG(name) AS noAccess
            FROM _names n
            WHERE NOT EXISTS (
              SELECT 1
              FROM _accessible a
              WHERE n.name = a. name
            );`
    return query
  }

  getStorageSetupQueries(options?: any) {
    const [cartoTempDatabase, cartoTempSchema] = this.tempLocation.split('.')
    const [cartoWorkspaceDatabase, cartoWorkspaceSchema] = this.workspaceLocation.split('.')
    const [cartoExtensionsDatabase, cartoExtensionsSchema] = this.extensionsLocation.split('.')
    return [
      dedent`
      DECLARE
        schemaExists BOOLEAN;
        messageCartoTemp STRING DEFAULT '' ;
        messageCartoWorkspace STRING DEFAULT '';
        messageCartoExtensions STRING DEFAULT '';
        res RESULTSET;
      BEGIN
        BEGIN
          SELECT COUNT(*) != 0 INTO :schemaExists FROM ${cartoTempDatabase}.INFORMATION_SCHEMA.SCHEMATA WHERE SCHEMA_NAME ILIKE '${cartoTempSchema}';
          IF (schemaExists) THEN
            BEGIN
              CREATE TABLE IF NOT EXISTS ${this.tempLocation}.__TESTTABLE AS (SELECT 1 AS N);
              DROP TABLE IF EXISTS ${this.tempLocation}.__TESTTABLE;
              messageCartoTemp := '{"location": "carto_temp", "success": true}';
            EXCEPTION WHEN OTHER THEN
              messageCartoTemp := '{"location": "carto_temp", "success": false, "error": "temp_storage_create_tables"}';
            END;
          ELSE
            BEGIN
              CREATE SCHEMA IF NOT EXISTS ${this.tempLocation};
              messageCartoTemp := '{"location": "carto_temp", "success": true}';
            EXCEPTION WHEN OTHER THEN
              messageCartoTemp := '{"location": "carto_temp", "success": false, "error": "temp_storage_access_create"}';
            END;
          END IF;
        EXCEPTION WHEN OTHER THEN
          messageCartoTemp := '{"location": "carto_temp", "success": false, "error": "temp_storage_access_create"}';
        END;

        BEGIN
          SELECT COUNT(*) != 0 INTO :schemaExists FROM ${cartoWorkspaceDatabase}.INFORMATION_SCHEMA.SCHEMATA WHERE SCHEMA_NAME ILIKE '${cartoWorkspaceSchema}';
          IF (schemaExists) THEN
            BEGIN
              CREATE TABLE IF NOT EXISTS ${this.workspaceLocation}.__TESTTABLE AS (SELECT 1 AS N);
              DROP TABLE IF EXISTS ${this.workspaceLocation}.__TESTTABLE;
              messageCartoWorkspace := '{"location": "carto_workspace", "success": true}';
            EXCEPTION WHEN OTHER THEN
              messageCartoWorkspace := '{"location": "carto_workspace", "success": false, "error": "temp_storage_create_tables"}';
            END;
          ELSE
            BEGIN
              CREATE SCHEMA IF NOT EXISTS ${this.workspaceLocation};
              messageCartoWorkspace := '{"location": "carto_workspace", "success": true}';
            EXCEPTION WHEN OTHER THEN
              messageCartoWorkspace := '{"location": "carto_workspace", "success": false, "error": "temp_storage_access_create"}';
            END;
          END IF;
        EXCEPTION WHEN OTHER THEN
          messageCartoWorkspace := '{"location": "carto_workspace", "success": false, "error": "temp_storage_access_create"}';
        END;

        BEGIN
          SELECT COUNT(*) != 0 INTO :schemaExists FROM ${cartoExtensionsDatabase}.INFORMATION_SCHEMA.SCHEMATA WHERE SCHEMA_NAME ILIKE '${cartoExtensionsSchema}';
          IF (schemaExists) THEN
            BEGIN
              CREATE TABLE IF NOT EXISTS ${this.extensionsLocation}.__TESTTABLE AS (SELECT 1 AS N);
              DROP TABLE IF EXISTS ${this.extensionsLocation}.__TESTTABLE;
              messageCartoExtensions := '{"location": "carto_extensions", "success": true}';
            EXCEPTION WHEN OTHER THEN
              messageCartoExtensions := '{"location": "carto_extensions", "success": false, "error": "temp_storage_create_tables"}';
            END;
          ELSE
            BEGIN
              CREATE SCHEMA IF NOT EXISTS ${this.extensionsLocation};
              messageCartoExtensions := '{"location": "carto_extensions", "success": true}';
            EXCEPTION WHEN OTHER THEN
              messageCartoExtensions := '{"location": "carto_extensions", "success": false, "error": "temp_storage_access_create"}';
            END;
          END IF;
        EXCEPTION WHEN OTHER THEN
          messageCartoExtensions := '{"location": "carto_extensions", "success": false, "error": "temp_storage_access_create"}';
        END;

        res := (SELECT CONCAT('[', :messageCartoTemp, ',', :messageCartoWorkspace, ',', :messageCartoExtensions, ']') as "result");
        RETURN TABLE(res);
      END`
    ]
  }

  getTempStorageCleanUpQuery(): string | null {
    const [database, schema] = this.tempLocation.split('.')
    return dedent`
      DECLARE
        statements STRING;
      BEGIN
        LET res resultset := (EXECUTE IMMEDIATE '
          SELECT
            LISTAGG(''DROP TABLE ''|| TABLE_CATALOG || ''.'' || TABLE_SCHEMA || ''.'' || TABLE_NAME || '';'')
          FROM
            ${this.applyCapitalization(database)}.INFORMATION_SCHEMA.TABLES
          WHERE TABLE_SCHEMA = ''${this.applyCapitalization(schema)}''
          AND CREATED < DATEADD(''day'', -30, CURRENT_TIMESTAMP)
        ');
        LET cur_res CURSOR FOR res;
        OPEN cur_res;
        FETCH cur_res INTO statements;
        IF (statements != '') THEN
          EXECUTE IMMEDIATE 'BEGIN\\n' || :statements || '\\nEND';
        END IF;
      END;`
  }

  getStoredProcedureCacheInvalidationQueries(name: string): Array<string> {
    const tablePrefix = this.getStoredProcedureOutputTableNameForProcedureName(name.split('.').pop() as string)
    const [database, schema] = this.workspaceLocation.split('.')
    return [
      dedent`
      DECLARE
        statements STRING;
      BEGIN
        LET res resultset := (EXECUTE IMMEDIATE '
          SELECT
            LISTAGG(''DROP TABLE ''|| TABLE_CATALOG || ''.'' || TABLE_SCHEMA || ''.'' || TABLE_NAME || '';'')
          FROM
            ${this.applyCapitalization(database)}.INFORMATION_SCHEMA.TABLES
          WHERE TABLE_SCHEMA = ''${this.applyCapitalization(schema)}''
          AND TABLE_NAME ILIKE ''${tablePrefix}%''
        ');
        LET cur_res CURSOR FOR res;
        OPEN cur_res;
        FETCH cur_res INTO statements;
        IF (statements != '') THEN
          EXECUTE IMMEDIATE 'BEGIN\\n' || :statements || '\\nEND';
        END IF;
      END;`
    ]
  }

  getRemoveStoredProcedureQueries(name: string): Array<string> {
    const [database, schema, procedureName] = name.split('.')

    return [
      dedent`
      DECLARE
        procedureSignature STRING;
        dropStatement STRING;
        paramTypes STRING;
      BEGIN
        SELECT ARGUMENT_SIGNATURE INTO :procedureSignature
        FROM ${database}.INFORMATION_SCHEMA.PROCEDURES
        WHERE PROCEDURE_NAME = '${procedureName.toUpperCase()}'
          AND PROCEDURE_SCHEMA = '${schema.toUpperCase()}'
        LIMIT 1;

        IF (procedureSignature IS NOT NULL) THEN
          paramTypes := REGEXP_REPLACE(
            :procedureSignature,
            '([A-Z_][A-Z0-9_$]*|"[^"]+")\\s+',
            ''
          );
          dropStatement := 'DROP PROCEDURE IF EXISTS ${this.quoteName(name)}' || :paramTypes;
        ELSE
          dropStatement := 'DROP PROCEDURE IF EXISTS ${this.quoteName(name)}()';
        END IF;

        EXECUTE IMMEDIATE :dropStatement;
      END;`
    ]
  }

  getTableListQuery(filter: string, locationType: LocationType = LocationType.Temp): string | null {
    const [database, schema] = this.applyCapitalization(this.getLocation(locationType)).split('.')
    filter = this.applyCapitalization(filter)
    return dedent`
      SELECT TABLE_NAME AS "table_name"
      FROM ${database}.INFORMATION_SCHEMA.TABLES
      WHERE TABLE_SCHEMA = '${schema}' AND TABLE_NAME LIKE '${filter}';`
  }

  dataProviderTypeToColumnType(type: string): ColumnDataType {
    switch (type) {
      case 'NUMBER':
      case 'FLOAT':
        return ColumnDataType.Number
      case 'BOOLEAN':
        return ColumnDataType.Boolean
      case 'GEOMETRY':
        return ColumnDataType.Geometry
      case 'GEOGRAPHY':
        return ColumnDataType.Geography
      case 'DATE':
        return ColumnDataType.Date
      case 'TIME':
        return ColumnDataType.Time
      case 'TIMESTAMP':
      case 'TIMESTAMPTZ':
        return ColumnDataType.Timestamp
      case 'BINARY':
        return ColumnDataType.Binary
      default:
        return ColumnDataType.String
    }
  }

  numericTypeToDataProviderType(numericType: NumericType) {
    return numericType === NumericType.Float ? 'FLOAT' : 'INT'
  }

  quoteVariableName(name: string) {
    return `"${name.toUpperCase()}"`
  }

  applyCapitalization(tablename: string): string {
    return tablename.toUpperCase()
  }

  getExtensionComponentsQuery(): string | null {
    const extensionsTable = `${this.quoteName(this.extensionsLocation)}.${EXTENSIONS_TABLE_NAME}`
    return dedent`
          SELECT
            name AS "name", metadata AS "metadata"
          FROM
            ${extensionsTable}
          `
  }

  getExtensionUninstallQuery(name: string) {
    return dedent`
      DECLARE
          procedures STRING;
      BEGIN
          BEGIN
            procedures := (
                SELECT procedures
                FROM ${this.quoteName(this.extensionsLocation)}.${EXTENSIONS_TABLE_NAME}
                WHERE name = '${name}'
            );
          EXCEPTION
            WHEN OTHER THEN
              procedures := '';
          END;
          BEGIN
              EXECUTE IMMEDIATE 'DROP PROCEDURE IF EXISTS ${this.quoteName(this.extensionsLocation)}.'
                  || REPLACE(
                      :procedures,
                      ';',
                      ';DROP PROCEDURE IF EXISTS ${this.quoteName(this.extensionsLocation)}.'
                    );
          EXCEPTION
              WHEN OTHER THEN
                  NULL;
          END;
          DELETE FROM ${this.quoteName(this.extensionsLocation)}.${EXTENSIONS_TABLE_NAME}
          WHERE name = '${name}';
        END;`
  }

  adaptComponent(component: WorkflowComponent) {
    const whiteList = ['native.geometrytogeography']
    if (!whiteList.includes(component.metadata.name)) {
      for (const input of component.inputs) {
        if (input.type === WorkflowComponentParamType.Column && input.dataType) {
          input.dataType = (input.dataType as Array<ColumnDataType>).filter((x) => x !== ColumnDataType.Geometry)
        }
      }
    }
    component.inputs = component.inputs.filter((x) => x.providers == null || x.providers.includes(Provider.Snowflake))
  }

  getOutputSchemasQueries(tableNames: Array<string>): string {
    const tempLocation = this.tempLocation.toUpperCase()
    const database = tempLocation.split('.')[0]
    const schema = tempLocation.split('.')[1]
    return dedent`
      WITH cols AS (
        SELECT
          table_name,
          column_name AS name,
          data_type AS db_type,
          CASE data_type
            WHEN 'NUMBER' THEN '${ColumnDataType.Number}'
            WHEN 'FLOAT' THEN '${ColumnDataType.Number}'
            WHEN 'BOOLEAN' THEN '${ColumnDataType.Boolean}'
            WHEN 'GEOGRAPHY' THEN '${ColumnDataType.Geography}'
            WHEN 'GEOMETRY' THEN '${ColumnDataType.Geometry}'
            WHEN 'DATE' THEN '${ColumnDataType.Date}'
            WHEN 'TIME' THEN '${ColumnDataType.Time}'
            WHEN 'DATETIME' THEN '${ColumnDataType.Timestamp}'
            WHEN 'TIMESTAMP_LTZ' THEN '${ColumnDataType.Timestamp}'
            WHEN 'TIMESTAMP_NTZ' THEN '${ColumnDataType.Timestamp}'
            WHEN 'TIMESTAMP' THEN '${ColumnDataType.Timestamp}'
            WHEN 'TIMESTAMP_TZ' THEN '${ColumnDataType.Timestamp}'
            WHEN 'TIMESTAMPTZ' THEN '${ColumnDataType.Timestamp}'
            WHEN 'BINARY' THEN '${ColumnDataType.Binary}'
            ELSE '${ColumnDataType.String}'
          END AS type,
          ordinal_position
        FROM ${database}.INFORMATION_SCHEMA.COLUMNS
        WHERE
          table_name IN (${tableNames.map((t) => `'${t.toUpperCase()}'`).join(', ')})
          AND table_schema = '${schema}'
      )
      SELECT
        CONCAT('${tempLocation}.', table_name) AS fqn,
        TO_JSON(
          ARRAY_AGG(
            OBJECT_CONSTRUCT(
              'name', name,
              'type', type,
              'db_type', db_type
            )
          )
          WITHIN GROUP (ORDER BY ordinal_position ASC)
        ) AS schema
      FROM cols
      GROUP BY table_name;`
  }

  prepareSchemaQueries(queries: Array<string>): Array<string> {
    const last = queries.pop() as string
    return [`BEGIN\n${queries.join('\n')}\nEND;`, last]
  }
}
